import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_SEARCH_DEBOUNCE_MS, DEFAULT_SELECTOR_PAGE_SIZE } from '../constants';
import type { AsyncSearchParams, PagedResult, PickFilterValues, SortState } from '../types';

export { DEFAULT_SEARCH_DEBOUNCE_MS } from '../constants';

export interface UsePagedAsyncSuggestionsOptions<TItem> {
  /**
   * Stable react-query key factory — capability-local, owner of cache vrstvy
   * je entitní wrapper (např. `DestinationSelector`).
   * Klíč MUSÍ obsahovat `query/page/pageSize/sort` deterministicky.
   */
  queryKey: (params: AsyncSearchParams) => readonly unknown[];
  /**
   * Načítání návrhů z entitní search API. Wrapper si drží konkrétní endpoint
   * a mapování; hook sám query znormalizuje (trim).
   */
  search: (params: AsyncSearchParams) => Promise<PagedResult<TItem>>;
  /** Pokud `false`, hook neprovádí dotaz (popup zavřený, disabled). */
  enabled?: boolean;
  /** Iniciální hodnota query inputu. */
  initialQuery?: string;
  /** Iniciální stránka (default 1). */
  initialPage?: number;
  /** Iniciální velikost stránky (default {@link DEFAULT_SELECTOR_PAGE_SIZE}). */
  initialPageSize?: number;
  /** Iniciální sort stav (default undefined = žádný sort). */
  initialSort?: SortState;
  /** Při změně hodnoty se resetuje stránka na 1 (např. pick filter values). */
  filterResetKey?: PickFilterValues;
  /**
   * Debounce query před odesláním do API (ms). Input reaguje okamžitě, fetch až po prodlevě.
   * `0` = bez debounce (vhodné pro testy).
   */
  debounceMs?: number;
}

export interface UsePagedAsyncSuggestionsResult<TItem> {
  query: string;
  setQuery: (value: string) => void;
  reset: () => void;
  page: number;
  setPage: (value: number) => void;
  pageSize: number;
  setPageSize: (value: number) => void;
  sort: SortState | undefined;
  setSort: (value: SortState | undefined) => void;
  items: readonly TItem[];
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Generický async-suggestion hook capability `shared/ui/async-selector` s podporou
 * server-side paging a sortu.
 *
 * Vlastní state query inputu, page, pageSize a sort. Deleguje cache na react-query
 * přes `queryKey` funkci, kterou dodává entitní wrapper (FR-061). Hook nikdy nezná
 * konkrétní entitu ani transport — je čistě tabulkou „params → paged items".
 *
 * **Chování:**
 * - Input `query` se mění okamžitě; API dostane trimnutou hodnotu až po `debounceMs` (default 300 ms).
 * - Na změnu `query` se automaticky resetne `page` na 1 (aby uživatel viděl výsledky od začátku).
 * - `queryKey` obsahuje `query/page/pageSize/sort` deterministicky pro správné cachování.
 * - `enabled=false` zabraňuje dotazování (např. zavřený popover); při `enabled=true`
 *   se dotazuje i pro `query=""` (první stránka default).
 */
export function usePagedAsyncSuggestions<TItem>(
  options: UsePagedAsyncSuggestionsOptions<TItem>
): UsePagedAsyncSuggestionsResult<TItem> {
  const {
    queryKey,
    search,
    enabled = true,
    initialQuery = '',
    initialPage = 1,
    initialPageSize = DEFAULT_SELECTOR_PAGE_SIZE,
    initialSort,
    filterResetKey,
    debounceMs = DEFAULT_SEARCH_DEBOUNCE_MS,
  } = options;

  const [query, setQueryRaw] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<SortState | undefined>(initialSort);

  useEffect(() => {
    if (debounceMs <= 0) {
      setDebouncedQuery(query);
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Reset page na 1 při změně query (aby uživatel viděl výsledky od začátku).
  // Řešíme přímo v setQuery wrapperu, ne přes useEffect — eliminuje race condition
  // (jeden zbytečný fetch se starým page před proběhnutím efektu).
  const setQuery = (value: string) => {
    setQueryRaw(value);
    setPage(1);
  };

  const filterResetSerialized = filterResetKey == null ? null : JSON.stringify(filterResetKey);
  const prevFilterResetSerialized = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (filterResetSerialized == null) return;
    if (prevFilterResetSerialized.current === undefined) {
      prevFilterResetSerialized.current = filterResetSerialized;
      return;
    }
    if (prevFilterResetSerialized.current === filterResetSerialized) return;
    prevFilterResetSerialized.current = filterResetSerialized;
    setPage(1);
  }, [filterResetSerialized]);

  const normalizedQuery = debouncedQuery.trim();

  const params: AsyncSearchParams = {
    query: normalizedQuery,
    page,
    pageSize,
    sort,
  };

  // `search` je dodaný entitním wrapperem; kontrakt cache je vlastněný `queryKey`
  // funkcí (FR-066), takže `search` nepatří do query key — jeho identita se mezi
  // rendery mění s closure parametry (queryClient apod.), ale request payload
  // jednoznačně určuje `params`.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKey(params),
    queryFn: () => search(params),
    enabled,
    placeholderData: keepPreviousData,
  });

  const reset = () => {
    setQueryRaw('');
    setDebouncedQuery('');
    setPage(1);
  };

  return {
    query,
    setQuery,
    reset,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    items: data?.items ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isError,
  };
}
